import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { SpecificTaxMode } from '@efacture/core';
import type {
  ArticleWire,
  ArticleListResponseWire,
  ArticleStatisticsWire,
  ArticleCategoryWire,
  ArticleTypeWire,
  ArticlePriceModeWire,
} from '@efacture/core';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
//
// The response shapes are the shared contracts from @efacture/core
// (docs/ARCHITECTURE.md, "API↔web response contracts"), aliased under
// this module's names.

/** Code of the article-type table (DGI §7.5 codes plus the tenant's rows, §2.35). */
export type ArticleType = ArticleTypeWire;
/** The two values `ArticleWire.priceMode`/`CreateArticleData.priceMode` ever hold. */
export type PriceMode = ArticlePriceModeWire;
export type Article = ArticleWire;

export interface ArticleListParams {
  page?: number | undefined;
  limit?: number | undefined;
  search?: string | undefined;
  category?: string | undefined;
  articleType?: ArticleType | undefined;
  includeInactive?: boolean | undefined;
  favoritesOnly?: boolean | undefined;
  /** Only articles with stock tracking enabled (server-side filter). */
  stockManagedOnly?: boolean | undefined;
}

export interface CreateArticleData {
  code: string;
  name: string;
  description?: string | undefined;
  /** Unique per tenant; EAN-8 / UPC-A / EAN-13 carry their check digit. */
  barcode?: string | undefined;
  articleType: ArticleType;
  category?: string | undefined;
  taxGroup: string;
  psvbGroup?: string | undefined;
  /** Boisson soumise à la TSB 10% (DGI §6.8) */
  isBeverage?: boolean | undefined;
  /**
   * Per-article specific tax (DGI §2.17): one mode, its parameter (rate as
   * a fraction, or unit amount in FCFA) and the printed label. All four are
   * null when the article carries none.
   */
  specificTaxMode?: SpecificTaxMode | null | undefined;
  specificTaxRate?: number | null | undefined;
  specificTaxUnitAmount?: number | null | undefined;
  specificTaxLabel?: string | null | undefined;
  priceMode: PriceMode;
  unitPrice: number;
  unit: string;
  isStockManaged?: boolean | undefined;
  stockQty?: number | undefined;
  minStockAlert?: number | undefined;
  maxStock?: number | undefined;
  serviceNotes?: string | undefined;
  isActive?: boolean | undefined;
  isFavorite?: boolean | undefined;
  sortOrder?: number | undefined;
}

export interface UpdateArticleData {
  code?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  /** The empty string clears it. */
  barcode?: string | undefined;
  articleType?: ArticleType | undefined;
  category?: string | undefined;
  taxGroup?: string | undefined;
  psvbGroup?: string | undefined;
  /** Boisson soumise à la TSB 10% (DGI §6.8) */
  isBeverage?: boolean | undefined;
  /**
   * Per-article specific tax (DGI §2.17): one mode, its parameter (rate as
   * a fraction, or unit amount in FCFA) and the printed label. All four are
   * null when the article carries none.
   */
  specificTaxMode?: SpecificTaxMode | null | undefined;
  specificTaxRate?: number | null | undefined;
  specificTaxUnitAmount?: number | null | undefined;
  specificTaxLabel?: string | null | undefined;
  priceMode?: PriceMode | undefined;
  unitPrice?: number | undefined;
  unit?: string | undefined;
  isStockManaged?: boolean | undefined;
  stockQty?: number | undefined;
  minStockAlert?: number | undefined;
  maxStock?: number | undefined;
  serviceNotes?: string | undefined;
  isActive?: boolean | undefined;
  isFavorite?: boolean | undefined;
  sortOrder?: number | undefined;
  /** The `updatedAt` of the article the form loaded: a stale save is refused (409). */
  updatedAt?: string | undefined;
}

export interface UpdateArticlePriceData {
  unitPrice: number;
  priceMode?: PriceMode;
}

export type ArticleCategory = ArticleCategoryWire;
export type ArticleListResponse = ArticleListResponseWire;
export type ArticleStatistics = ArticleStatisticsWire;

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const articleKeys = {
  all: ['articles'] as const,
  lists: () => [...articleKeys.all, 'list'] as const,
  list: (params: ArticleListParams) => [...articleKeys.lists(), params] as const,
  details: () => [...articleKeys.all, 'detail'] as const,
  detail: (id: string) => [...articleKeys.details(), id] as const,
  byCode: (code: string) => [...articleKeys.all, 'byCode', code] as const,
  categories: () => [...articleKeys.all, 'categories'] as const,
  lowStock: () => [...articleKeys.all, 'lowStock'] as const,
  statistics: () => [...articleKeys.all, 'statistics'] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchArticles(params: ArticleListParams): Promise<ArticleListResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page: params.page,
    limit: params.limit,
    search: params.search,
    category: params.category,
    articleType: params.articleType,
    includeInactive: params.includeInactive,
    favoritesOnly: params.favoritesOnly,
    stockManagedOnly: params.stockManagedOnly,
  };
  return apiClient.get<ArticleListResponse>('/articles', queryParams);
}

async function fetchArticle(id: string): Promise<Article> {
  return apiClient.get<Article>(`/articles/${id}`);
}

async function fetchArticleByCode(code: string): Promise<Article> {
  return apiClient.get<Article>(`/articles/by-code/${encodeURIComponent(code)}`);
}

async function createArticle(data: CreateArticleData): Promise<Article> {
  return apiClient.post<Article>('/articles', data);
}

async function updateArticle(id: string, data: UpdateArticleData): Promise<Article> {
  return apiClient.put<Article>(`/articles/${id}`, data);
}

async function updateArticlePrice(id: string, data: UpdateArticlePriceData): Promise<Article> {
  return apiClient.put<Article>(`/articles/${id}/price`, data);
}

async function deleteArticle(id: string): Promise<void> {
  await apiClient.delete(`/articles/${id}`);
}

async function toggleFavorite(id: string): Promise<Article> {
  return apiClient.post<Article>(`/articles/${id}/toggle-favorite`);
}

async function fetchCategories(): Promise<ArticleCategory[]> {
  return apiClient.get<ArticleCategory[]>('/articles/categories');
}

async function fetchLowStockArticles(): Promise<Article[]> {
  return apiClient.get<Article[]>('/articles/low-stock');
}

async function fetchStatistics(): Promise<ArticleStatistics> {
  return apiClient.get<ArticleStatistics>('/articles/statistics');
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch a paginated list of articles
 * GET /api/articles
 */
export function useArticles(params: ArticleListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: articleKeys.list(params),
    queryFn: () => fetchArticles(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The articles of `ids`, one GET /api/articles/:id each; `settled` once every
 * request has answered, an article that could not be read left out.
 */
export function useArticlesById(ids: readonly string[]): { byId: Map<string, Article>; settled: boolean } {
  const queries = useQueries({
    queries: ids.map((id) => ({ queryKey: articleKeys.detail(id), queryFn: () => fetchArticle(id), retry: false })),
  });
  const byId = new Map<string, Article>();
  ids.forEach((id, index) => {
    const article = queries[index]?.data;
    if (article) byId.set(id, article);
  });
  return { byId, settled: queries.every((query) => !query.isPending) };
}

/**
 * Hook to fetch a single article by ID
 * GET /api/articles/:id
 */
export function useArticle(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: articleKeys.detail(id),
    queryFn: () => fetchArticle(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/**
 * Hook to fetch article categories
 * GET /api/articles/categories
 */
export function useArticleCategories(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: articleKeys.categories(),
    queryFn: fetchCategories,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch low stock articles
 * GET /api/articles/low-stock
 */
export function useLowStockArticles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: articleKeys.lowStock(),
    queryFn: fetchLowStockArticles,
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/**
 * Downloads the tenant's active articles as CSV, in the columns of the
 * article import (GET /api/articles/export).
 */
export function downloadArticlesCsv(): Promise<void> {
  return apiClient.download('/articles/export', 'articles.csv');
}

/**
 * Hook to create a new article
 * POST /api/articles
 */
export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateArticleData) => createArticle(data),
    onSuccess: () => {
      // Invalidate article lists to refetch
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.categories() });
      queryClient.invalidateQueries({ queryKey: articleKeys.statistics() });
    },
  });
}

/**
 * Hook to update an article
 * PUT /api/articles/:id
 */
export function useUpdateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateArticleData }) => updateArticle(id, data),
    onSuccess: (updatedArticle) => {
      // Update the specific article in cache
      queryClient.setQueryData(articleKeys.detail(updatedArticle.id), updatedArticle);
      if (updatedArticle.code) {
        queryClient.setQueryData(articleKeys.byCode(updatedArticle.code), updatedArticle);
      }
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.categories() });
      queryClient.invalidateQueries({ queryKey: articleKeys.statistics() });
    },
  });
}

/**
 * Hook to update article price only
 * PUT /api/articles/:id/price
 */
export function useUpdateArticlePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateArticlePriceData }) =>
      updateArticlePrice(id, data),
    onSuccess: (updatedArticle) => {
      // Update the specific article in cache
      queryClient.setQueryData(articleKeys.detail(updatedArticle.id), updatedArticle);
      if (updatedArticle.code) {
        queryClient.setQueryData(articleKeys.byCode(updatedArticle.code), updatedArticle);
      }
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
    },
  });
}

/**
 * Hook to soft delete an article
 * DELETE /api/articles/:id
 */
export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteArticle(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: articleKeys.detail(deletedId) });
      // Invalidate lists and statistics
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.categories() });
      queryClient.invalidateQueries({ queryKey: articleKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: articleKeys.lowStock() });
    },
  });
}

/**
 * Hook to toggle article favorite status
 * POST /api/articles/:id/toggle-favorite
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => toggleFavorite(id),
    onSuccess: (updatedArticle) => {
      // Update the specific article in cache
      queryClient.setQueryData(articleKeys.detail(updatedArticle.id), updatedArticle);
      if (updatedArticle.code) {
        queryClient.setQueryData(articleKeys.byCode(updatedArticle.code), updatedArticle);
      }
      // Invalidate lists to update favorite status in list views
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.statistics() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchArticles,
  fetchArticle,
  fetchArticleByCode,
  createArticle,
  updateArticle,
  updateArticlePrice,
  deleteArticle,
  toggleFavorite,
  fetchCategories,
  fetchLowStockArticles,
  fetchStatistics,
};
